/**
 * Agent Health Check Service
 *
 * Checks agent health endpoints with retry logic, caches versions, and provides health status.
 */

import type { AgentId, AgentHealthState } from '../components/AISceneChatbot/agents/agents.types';
import {
	getAgent,
	getAgentHealthEndpoint,
	extractVersionFromHealth,
	isHealthResponseOk,
} from '../components/AISceneChatbot/agents/agents.config';
import { version as appVersion } from '../../package.json';
import { buildAppVersionHealthUrl, extractAppCompatibility } from './app-version-compatibility';

const CACHE_KEY = 'agent_health_cache';
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes
const MAX_RETRIES = 5;
const INITIAL_BACKOFF_MS = 500; // 500ms, 1s, 2s, 4s, 8s

// In-memory cache for current session
const healthCache: Record<string, AgentHealthState> = {};

// Track ongoing health checks to report loading state
type HealthCheckCallback = (isLoading: boolean) => void;
let onLoadingChange: HealthCheckCallback | null = null;

export function setHealthCheckLoadingCallback(callback: HealthCheckCallback | null) {
	onLoadingChange = callback;
}

async function sleep(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchHealthWithRetry(
	endpoint: string,
	agentId: AgentId,
): Promise<{ success: true; data: unknown } | { success: false; error: string }> {
	let lastError = '';

	for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
		try {
			const response = await fetch(endpoint, {
				method: 'GET',
				headers: { 'Content-Type': 'application/json' },
			});

			if (!response.ok) {
				throw new Error(`HTTP ${response.status}`);
			}

			const data = await response.json();
			return { success: true, data };
		} catch (error) {
			lastError = error instanceof Error ? error.message : 'Health check failed';

			// Don't wait after the last attempt
			if (attempt < MAX_RETRIES - 1) {
				const backoffMs = INITIAL_BACKOFF_MS * Math.pow(2, attempt);
				await sleep(backoffMs);
			}
		}
	}

	return { success: false, error: lastError };
}

export async function checkAgentHealth(agentId: AgentId): Promise<AgentHealthState> {
	const healthEndpoint = getAgentHealthEndpoint(agentId);
	const endpoint = healthEndpoint ? buildAppVersionHealthUrl(healthEndpoint, appVersion) : null;
	const agent = getAgent(agentId);

	// Agent has no endpoint for either health or app compatibility checks.
	if (!endpoint) {
		return {
			status: 'healthy',
			version: agent.version,
			minimumAppVersion: null,
			isAppVersionSupported: null,
			lastChecked: Date.now(),
			error: null,
		};
	}

	// Signal loading started
	onLoadingChange?.(true);

	try {
		const result = await fetchHealthWithRetry(endpoint, agentId);

		if (result.success) {
			const version = extractVersionFromHealth(agentId, result.data);
			const isHealthy = !agent.supportsHealthCheck || isHealthResponseOk(agentId, result.data);
			const { minimumAppVersion, isAppVersionSupported } = extractAppCompatibility(result.data);

			const state: AgentHealthState = {
				status: isHealthy ? 'healthy' : 'unhealthy',
				version: version || agent.version,
				minimumAppVersion,
				isAppVersionSupported,
				lastChecked: Date.now(),
				error: isHealthy ? null : 'Service reported unhealthy',
			};

			// Cache successful result
			healthCache[agentId] = state;
			persistCache();

			return state;
		}

		// All retries failed - try to get cached version
		const failedResult = result as { success: false; error: string };
		const errorMessage = failedResult.error;
		const cached = getCachedHealth(agentId);
		if (cached) {
			return {
				...cached,
				status: agent.supportsHealthCheck ? 'unhealthy' : 'healthy',
				minimumAppVersion: null,
				isAppVersionSupported: null,
				error: agent.supportsHealthCheck ? errorMessage : null,
			};
		}

		// Fallback to config version
		return {
			status: agent.supportsHealthCheck ? 'unhealthy' : 'healthy',
			version: agent.version,
			minimumAppVersion: null,
			isAppVersionSupported: null,
			lastChecked: Date.now(),
			error: agent.supportsHealthCheck ? errorMessage : null,
		};
	} finally {
		// Signal loading finished
		onLoadingChange?.(false);
	}
}

function getCachedHealth(agentId: AgentId): AgentHealthState | null {
	// Check in-memory cache first
	if (healthCache[agentId]) return healthCache[agentId];

	// Check localStorage
	try {
		const stored = localStorage.getItem(CACHE_KEY);
		if (stored) {
			const parsed = JSON.parse(stored);
			if (parsed[agentId] && Date.now() - parsed[agentId].lastChecked < CACHE_TTL) {
				healthCache[agentId] = parsed[agentId];
				return parsed[agentId];
			}
		}
	} catch {
		// Ignore localStorage errors
	}
	return null;
}

function persistCache() {
	try {
		localStorage.setItem(CACHE_KEY, JSON.stringify(healthCache));
	} catch {
		// Ignore localStorage errors
	}
}

export function getAgentDisplayVersion(agentId: AgentId): string {
	const cached = healthCache[agentId];
	if (cached?.version) return cached.version;
	return getAgent(agentId).version;
}

export function isAgentHealthy(agentId: AgentId): boolean {
	const cached = healthCache[agentId];
	return cached?.status !== 'unhealthy';
}
